import { AxiosInstance } from 'axios'

import { StripeSession } from '../../../domain'
import { withGenericErrorHandling } from '../errors'

export interface PaymentCardClient {
  stripeMotoSettleSession: (
    accountId: string,
    request: StripeMotoCheckoutRequest,
  ) => Promise<void>
  stripeSettleSession: (
    accountId: string,
    request: StripeCheckoutRequest,
  ) => Promise<StripeSession>

  // TODO (MK): Check with Alex
  stripeSetupSession: (params: StripeSetupRequest) => Promise<StripeSession>

  stripeCheckoutMta: (
    adjustmentId: string,
    request: StripeCheckoutRequest,
  ) => Promise<StripeSession>

  stripeMotoCheckoutMta: (
    adjustmentId: string,
    request: StripeMotoCheckoutRequest,
  ) => Promise<void>

  stripeCheckoutProposal: (
    proposalCode: string,
    request: StripeCheckoutRequest,
  ) => Promise<StripeSession>

  stripeMotoCheckoutProposal: (
    proposalCode: string,
    request: StripeMotoCheckoutRequest,
  ) => Promise<void>
}

interface LineItem {
  name: string
  description?: string
  imageUrl: string | undefined
}

export interface StripeCheckoutRequest {
  successUrl: string
  cancelUrl: string
  lineItem: LineItem
  locale: string
  saveCard?: boolean
}

export interface StripeMotoCheckoutRequest {
  postalCode?: string
  country?: string
  token: string
  itemName: string
  saveCard?: boolean
}

export interface StripeMotoSettleError {
  type: 'timeout' | 'card_error' | 'invalid_request_error' | 'incomplete'
  chargeStatus?: string
  cardDeclineCode?: string
  message?: string
}

export interface StripeSetupRequest {
  clientReference?: string
  successUrl: string
  cancelUrl: string
}

export const createPaymentCardClient = (
  getInstance: () => Promise<AxiosInstance>,
): PaymentCardClient => ({
  stripeCheckoutMta: (adjustmentId, request) =>
    withGenericErrorHandling(async () => {
      const instance = await getInstance()

      const response = await instance.post<StripeSession>(
        `/stripe/checkout/mta/${encodeURIComponent(adjustmentId)}`,
        request,
      )
      return response.data
    }),
  stripeMotoCheckoutMta: (adjustmentId, request) =>
    withGenericErrorHandling(async () => {
      const instance = await getInstance()
      await instance.post<StripeSession>(
        `/stripe/moto/checkout/mta/${encodeURIComponent(adjustmentId)}`,
        request,
      )
    }),
  stripeCheckoutProposal: (proposalCode, request) =>
    withGenericErrorHandling(async () => {
      const instance = await getInstance()
      const response = await instance.post<StripeSession>(
        `/stripe/checkout/proposal/${encodeURIComponent(proposalCode)}`,
        request,
      )
      return response.data
    }),
  stripeMotoCheckoutProposal: (proposalCode, request) =>
    withGenericErrorHandling(async () => {
      const instance = await getInstance()
      await instance.post<StripeSession>(
        `/stripe/moto/checkout/proposal/${encodeURIComponent(proposalCode)}`,
        request,
      )
    }),
  stripeMotoSettleSession: (accountId, request) =>
    withGenericErrorHandling(async () => {
      const instance = await getInstance()
      await instance.post<StripeSession>(
        `/stripe/moto/settle/${encodeURIComponent(accountId)}`,
        request,
      )
    }),
  stripeSettleSession: (accountId, request) =>
    withGenericErrorHandling(async () => {
      const instance = await getInstance()
      const response = await instance.post<StripeSession>(
        `/stripe/settle/${encodeURIComponent(accountId)}`,
        request,
      )
      return response.data
    }),
  stripeSetupSession: (params) =>
    withGenericErrorHandling(async () => {
      const instance = await getInstance()
      const response = await instance.post<StripeSession>('/stripe/setup', params)
      return response.data
    }),
})
